/* Service sticker customiser - Capital Letter Signs (Oct 2026).
 *
 * Live preview of a 100 x 60 mm boiler service sticker, built from a template in SSC_TEMPLATES.
 * Everything is drawn as vector paths (text is turned into outlines with opentype.js), so the
 * preview IS the print file. On "Add to cart" the finished SVG is attached to the order as a
 * line-item file, with the typed details as readable properties.
 *
 * The template fixes WHERE everything goes. The customer's logo decides how it looks: colours,
 * icon style and font are worked out from the logo (styleFromLogo), and can be overridden.
 *
 * Needs: opentype.js, qrcode-generator, ssc-templates.js, ssc-gas-safe.js, and a container:
 *   <div class="ssc" data-ssc data-form-id="product-form-XXXX"></div>
 */
(function () {
  'use strict';

  var FONT_URL = 'https://cdn.jsdelivr.net/npm/@fontsource/{p}@5.0.8/files/{p}-latin-{w}-normal.woff';
  var WEIGHTS = { medium: 500, semibold: 600, bold: 700, extrabold: 800 };
  var FAMILIES = {
    poppins: { name: 'Clean', pkg: 'poppins' },
    montserrat: { name: 'Bold', pkg: 'montserrat' },
    slab: { name: 'Traditional', pkg: 'roboto-slab' },
    oswald: { name: 'Condensed', pkg: 'oswald', max: 700 }
  };
  var TYPES = {
    due: { name: 'Next service due', label: 'Your next service is due by...' },
    record: { name: 'Service record', label: 'Service record', rows: ['Date', 'Engineer', 'Next due'] },
    install: { name: 'Installation', label: 'Installed by', rows: ['Date', 'Engineer', 'Warranty until'] }
  };
  var STRAPLINES = ['24HR BREAKDOWN CALL OUT', 'BOILERS INSTALLED & SERVICED', 'GAS, HEATING & PLUMBING', 'LANDLORD GAS SAFETY CERTIFICATES', 'BOOK YOUR NEXT SERVICE WITH US'];
  var QR_CAPTIONS = ['Scan to book', 'Scan to review us', 'Scan for our website', ''];
  var MIN_DPI = 150;                        // below this the logo will print soft
  var SAVE_KEY = 'ssc-design-v2';
  var ICONS = {                             // Material Icons (Apache 2.0), 24 x 24
    phone: 'M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z',
    email: 'M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z',
    web: 'M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zm6.93 6h-2.95c-.32-1.25-.78-2.45-1.38-3.56 1.84.63 3.37 1.91 4.33 3.56zM12 4.04c.83 1.2 1.48 2.53 1.91 3.96h-3.82c.43-1.43 1.08-2.76 1.91-3.96zM4.26 14C4.1 13.36 4 12.69 4 12s.1-1.36.26-2h3.38c-.08.66-.14 1.32-.14 2 0 .68.06 1.34.14 2H4.26zm.82 2h2.95c.32 1.25.78 2.45 1.38 3.56-1.84-.63-3.37-1.9-4.33-3.56zm2.95-8H5.08c.96-1.66 2.49-2.93 4.33-3.56C8.81 5.55 8.35 6.75 8.03 8zM12 19.96c-.83-1.2-1.48-2.53-1.91-3.96h3.82c-.43 1.43-1.08 2.76-1.91 3.96zM14.34 14H9.66c-.09-.66-.16-1.32-.16-2 0-.68.07-1.35.16-2h4.68c.09.65.16 1.32.16 2 0 .68-.07 1.34-.16 2zm.25 5.56c.6-1.11 1.06-2.31 1.38-3.56h2.95c-.96 1.65-2.49 2.93-4.33 3.56zM16.36 14c.08-.66.14-1.32.14-2 0-.68-.06-1.34-.14-2h3.38c.16.64.26 1.31.26 2s-.1 1.36-.26 2h-3.38z'
  };

  /* ---------- fonts (each family loads the first time it's used) ---------- */
  var fonts = {}, loading = {};
  function loadFamily(id) {
    if (loading[id]) return loading[id];
    var fam = FAMILIES[id]; fonts[id] = {};
    loading[id] = Promise.all(Object.keys(WEIGHTS).map(function (k) {
      var w = Math.min(WEIGHTS[k], fam.max || 900);
      return opentype.load(FONT_URL.replace(/\{p\}/g, fam.pkg).replace('{w}', w)).then(function (f) { fonts[id][k] = f; });
    }));
    return loading[id];
  }
  var curFam = 'poppins';                   // set by render(); render is synchronous
  var tooSmall = null;                      // filled by render() for the main preview: fields printing too small to read
  var MIN_CAP = { phone: 2.2, email: 1.3, web: 1.3, callout: 1.9, boxLabel: 1.3, name: 2.4 };

  /* ---------- colour helpers ---------- */
  function hexToRgb(h) { h = h.replace('#', ''); return [0, 2, 4].map(function (i) { return parseInt(h.substr(i, 2), 16); }); }
  function rgbToHex(r) { return '#' + r.map(function (v) { return ('0' + Math.max(0, Math.min(255, Math.round(v))).toString(16)).slice(-2); }).join('').toUpperCase(); }
  function tint(hex, t) { var c = hexToRgb(hex); return rgbToHex(c.map(function (v) { return 255 - (255 - v) * t; })); }
  function lum(hex) { var c = hexToRgb(hex).map(function (v) { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); }); return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2]; }
  function contrast(a, b) { var x = lum(a), y = lum(b); return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05); }
  function hsl(rgb) {
    var r = rgb[0] / 255, g = rgb[1] / 255, b = rgb[2] / 255, mx = Math.max(r, g, b), mn = Math.min(r, g, b), l = (mx + mn) / 2, d = mx - mn, h = 0, s = 0;
    if (d) { s = d / (1 - Math.abs(2 * l - 1)); h = mx === r ? ((g - b) / d) % 6 : mx === g ? (b - r) / d + 2 : (r - g) / d + 4; h = (h * 60 + 360) % 360; }
    return { h: h, s: s, l: l };
  }
  function hueGap(a, b) { var d = Math.abs(a - b) % 360; return d > 180 ? 360 - d : d; }
  function darken(hex, f) { return rgbToHex(hexToRgb(hex).map(function (v) { return v * f; })); }
  function resolveColours(base) {
    var c = Object.assign({ white: '#FFFFFF' }, base);
    ['primary', 'secondary', 'accent', 'ink'].forEach(function (n) {
      [0.08, 0.1, 0.12, 0.15, 0.2, 0.3].forEach(function (t) { c['tint:' + n + ':' + t] = tint(base[n], t); });
    });
    return c;
  }
  function col(c, name) { return c[name] || name; }

  /* ---------- the logo decides the style ---------- */
  // Reads the logo's pixels: its main colours (biggest first) and how heavy its ink is.
  function analyseLogo(src) {
    return new Promise(function (resolve) {
      var img = new Image();
      img.onload = function () {
        var k = Math.min(1, 160 / Math.max(img.naturalWidth || 160, img.naturalHeight || 160));
        var w = Math.max(1, Math.round((img.naturalWidth || 160) * k)), h = Math.max(1, Math.round((img.naturalHeight || 160) * k));
        var cv = document.createElement('canvas'); cv.width = w; cv.height = h;
        var x = cv.getContext('2d'); x.drawImage(img, 0, 0, w, h);
        var p = x.getImageData(0, 0, w, h).data, buckets = {}, ink = 0, x0 = w, y0 = h, x1 = 0, y1 = 0;
        for (var i = 0; i < p.length; i += 4) {
          if (p[i + 3] < 140 || Math.min(p[i], p[i + 1], p[i + 2]) > 232) continue;
          ink++; var px = (i / 4) % w, py = Math.floor(i / 4 / w);
          if (px < x0) x0 = px; if (px > x1) x1 = px; if (py < y0) y0 = py; if (py > y1) y1 = py;
          var key = (p[i] >> 4) + ',' + (p[i + 1] >> 4) + ',' + (p[i + 2] >> 4), b = buckets[key] || (buckets[key] = { n: 0, r: 0, g: 0, b: 0 });
          b.n++; b.r += p[i]; b.g += p[i + 1]; b.b += p[i + 2];
        }
        var list = Object.keys(buckets).map(function (k) { var b = buckets[k]; return { n: b.n, rgb: [b.r / b.n, b.g / b.n, b.b / b.n] }; }).sort(function (a, b) { return b.n - a.n; });
        var merged = [];                    // fold anti-aliasing shades into their main colour
        list.forEach(function (c) {
          var hit = merged.filter(function (m) { var d = 0; for (var j = 0; j < 3; j++) d += Math.pow(m.rgb[j] - c.rgb[j], 2); return Math.sqrt(d) < 58; })[0];
          if (hit) hit.n += c.n; else merged.push({ n: c.n, rgb: c.rgb });
        });
        var colours = merged.filter(function (m) { return ink && m.n / ink > 0.03; }).map(function (m) { var s = hsl(m.rgb); return { hex: rgbToHex(m.rgb), share: m.n / ink, h: s.h, s: s.s, l: s.l }; });
        var area = Math.max(1, (x1 - x0 + 1) * (y1 - y0 + 1));
        // where the artwork actually is, so empty space inside the file doesn't push the logo off-centre
        var box = ink ? { x0: Math.max(0, x0 - 1.5) / w, y0: Math.max(0, y0 - 1.5) / h, x1: Math.min(w, x1 + 2.5) / w, y1: Math.min(h, y1 + 2.5) / h } : null;
        resolve({ colours: colours, weight: ink / area, fontHint: fontHint(src), box: box });
      };
      img.onerror = function () { resolve({ colours: [], weight: 0.25 }); };
      img.src = src;
    });
  }
  // SVG logos that still contain live text name their font - use it to pick the closest family.
  function fontHint(src) {
    var m = /^data:image\/svg\+xml;base64,(.*)$/.exec(src), txt = '';
    try { txt = m ? atob(m[1]) : /^data:image\/svg\+xml,/.test(src) ? decodeURIComponent(src.split(',')[1]) : ''; } catch (e) { return ''; }
    var fams = (txt.match(/font-family\s*[:=]\s*["']?([^;"'>]+)/gi) || []).join(' ').toLowerCase();
    if (/condensed|oswald|bebas|impact|league gothic|anton/.test(fams)) return 'oswald';
    if (/serif|georgia|times|garamond|playfair|baskerville|slab|rockwell|bodoni|cambria/.test(fams.replace(/sans-serif/g, ''))) return 'slab';
    if (/black|heavy|extrabold|montserrat|gotham|futura|arial black/.test(fams)) return 'montserrat';
    return '';
  }
  // Turns the logo analysis into a colour scheme, icon style and font.
  function styleFromLogo(a) {
    var cols = a.colours.slice().sort(function (x, y) { return y.share - x.share; });
    var chroma = cols.filter(function (c) { return c.s > 0.28 && c.l > 0.1 && c.l < 0.9; });
    var darks = cols.filter(function (c) { return c.l <= 0.22 || (c.s <= 0.28 && c.l < 0.45); });
    var usable = chroma.filter(function (c) { return contrast(c.hex, '#FFFFFF') >= 3; });
    var primary = usable[0] ? usable[0].hex : darks[0] ? darks[0].hex : chroma[0] ? darken(chroma[0].hex, 0.55) : '#1B1B1B';
    var pH = hsl(hexToRgb(primary)).h;
    var accentC = usable.filter(function (c) { return c.hex !== primary && hueGap(c.h, pH) > 35; }).sort(function (x, y) { return y.s - x.s; })[0];
    var light = chroma.filter(function (c) { return contrast(c.hex, '#FFFFFF') < 3; })[0];
    var accent = accentC ? accentC.hex : light ? darken(light.hex, 0.8) : primary;
    if (contrast(accent, '#FFFFFF') < 2.2) accent = darken(accent, 0.7);
    var secC = usable.filter(function (c) { return c.hex !== primary && c.hex !== accent; })[0];
    var secondary = secC ? secC.hex : accent !== primary && chroma.length > 1 ? accent : primary;
    var ink = darks[0] && contrast(darks[0].hex, '#FFFFFF') >= 7 ? darks[0].hex : contrast(primary, '#FFFFFF') >= 7 ? primary : '#1B1B1B';
    var mono = !chroma.length || (chroma.length === 1 && !accentC);
    return {
      colours: { primary: primary, secondary: secondary, accent: accent, ink: ink },
      iconStyle: mono ? 'ring' : 'circle',
      font: a.fontHint || (a.weight > 0.42 ? 'montserrat' : 'poppins')
    };
  }

  /* ---------- text to outlines ---------- */
  function esc(s) { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;'); }
  // Draws `str` with the top of its capitals at y, cap height `cap` mm, shrunk to maxW if needed.
  function textPath(str, spec, c, overrides) {
    if (!str) return { svg: '', w: 0 };
    var o = Object.assign({}, spec, overrides || {});
    var fam = fonts[o.family || curFam] || fonts.poppins;
    var f = fam[o.font] || fam.medium || fam.bold;
    // anything the font can't draw (emoji, odd symbols) would print as an empty box, so leave it out
    str = Array.from(str).filter(function (ch) { return ch === ' ' || f.charToGlyphIndex(ch) > 0; }).join('').replace(/\s+/g, ' ').trim();
    if (!str) return { svg: '', w: 0 };
    var capRatio = (f.tables.os2 && f.tables.os2.sCapHeight ? f.tables.os2.sCapHeight : f.unitsPerEm * 0.7) / f.unitsPerEm;
    var size = o.cap / capRatio;
    var w = f.getAdvanceWidth(str, size, { kerning: true });
    if (o.maxW && w > o.maxW) { size *= o.maxW / w; w = o.maxW; }
    if (tooSmall && o.minCap && o.warnKey && size * capRatio < o.minCap) tooSmall[o.warnKey] = true;
    var x = o.x; if (o.align === 'center') x -= w / 2; else if (o.align === 'right') x -= w;
    var base = o.y + size * capRatio + (o.capOffset || 0);
    var d = f.getPath(str, x, base, size, { kerning: true }).toPathData(3);
    return { svg: '<path fill="' + col(c, o.colour) + '" d="' + d + '"/>', w: w, x: x, size: size };
  }

  function icon(name, spec, c, style) {
    var r = spec.r, cx = spec.cx, cy = spec.cy, colour = col(c, spec.colour), out = '', glyph = '#FFFFFF', k = 0.56;
    if (style === 'plain') { glyph = colour; k = 0.95; }
    else if (style === 'ring') { out = '<circle cx="' + cx + '" cy="' + cy + '" r="' + (r - 0.22) + '" fill="none" stroke="' + colour + '" stroke-width="0.4"/>'; glyph = colour; k = 0.52; }
    else out = '<circle cx="' + cx + '" cy="' + cy + '" r="' + r + '" fill="' + colour + '"/>';
    var s = (r * 2 * k) / 24;
    return out + '<path fill="' + glyph + '" transform="translate(' + (cx - 12 * s) + ',' + (cy - 12 * s) + ') scale(' + s + ')" d="' + ICONS[name] + '"/>';
  }

  /* Top-right group: Gas Safe (far right), OFTEC, then the QR code, then any uploaded badges. */
  function cluster(S, st, c) {
    var G = S.gasSafe; if (!G) return { svg: '', left: Infinity };
    var g = window.SSC_GAS_SAFE, h = G.h, right = G.x + g.w / g.h * h, x = right, gap = 2.6, out = '', f = st.fields;
    var under = function (txt, cx, w) { return textPath(txt, { x: cx, y: G.y + h + 0.9, cap: G.regCap || 1.5, maxW: w + 1.6, align: 'center', font: 'semibold', colour: 'ink' }, c).svg; };
    if (f.gasSafe) {
      var gw = g.w / g.h * h; x -= gw;
      out += '<g transform="translate(' + x + ',' + G.y + ') scale(' + (h / g.h) + ')">' + g.paths.map(function (p) { return '<path fill="' + p.fill + '" d="' + p.d + '"/>'; }).join('') + '</g>';
      if (f.reg) out += under('Reg No. ' + f.reg, x + gw / 2, gw);
      x -= gap;
    }
    var o = window.SSC_OFTEC;
    if (f.oftec && o) {
      var ow = o.w / o.h * h; x -= ow;
      out += '<g transform="translate(' + x + ',' + G.y + ') scale(' + (h / o.h) + ')">' + o.paths.map(function (p) { return '<path fill="' + p.fill + '" d="' + p.d + '"/>'; }).join('') + '</g>';
      if (f.oftecReg) out += under('Reg No. ' + f.oftecReg, x + ow / 2, ow);
      x -= gap;
    }
    if (f.qr && window.qrcode) {
      try {
        var q = window.qrcode(0, 'M'); q.addData(f.qr); q.make();
        var n = q.getModuleCount(), m = h / n, qx = x - h, d = '';
        for (var r = 0; r < n; r++) for (var cc = 0; cc < n; cc++) if (q.isDark(r, cc)) d += 'M' + (qx + cc * m).toFixed(3) + ' ' + (G.y + r * m).toFixed(3) + 'h' + m.toFixed(3) + 'v' + m.toFixed(3) + 'h-' + m.toFixed(3) + 'z';
        var qc = contrast(c.ink, '#FFFFFF') >= 7 ? c.ink : '#000000';
        out += '<rect x="' + (qx - 0.8) + '" y="' + (G.y - 0.8) + '" width="' + (h + 1.6) + '" height="' + (h + 1.6) + '" fill="#FFFFFF"/><path fill="' + qc + '" d="' + d + '"/>';
        if (f.qrCaption) out += under(f.qrCaption, qx + h / 2, h);
        x = qx - gap;
      } catch (e) { /* too long for a QR code - the warning explains */ }
    }
    (st.badges || []).forEach(function (b) {
      var bw = Math.min(h * b.w / b.h, h * 1.8), bh = bw * b.h / b.w; x -= bw;
      out += '<image href="' + b.src + '" x="' + x.toFixed(3) + '" y="' + (G.y + (h - bh) / 2).toFixed(3) + '" width="' + bw.toFixed(3) + '" height="' + bh.toFixed(3) + '"/>';
      x -= gap;
    });
    return { svg: out, left: x === right ? Infinity : x + gap };
  }

  // Box contents for each sticker type.
  function boxRows(B, st, c) {
    var D = B.dates, out = '', T = TYPES[st.type] || TYPES.due;
    var top = B.label.y + B.label.cap + 1.6, bot = B.y + B.h - 1.6, step = (bot - top) / 3;
    if (!T.rows) {                          // three pre-printed years, day and month written in by hand
      var y0 = new Date().getFullYear() % 100;
      for (var i = 0; i < 3; i++) {
        var yr = String(y0 + 1 + i), cy = top + step * (i + 0.5), ty = cy - D.cap / 2;
        var slash = textPath('/', { x: 0, y: ty, cap: D.cap, font: D.font, colour: D.colour }, c);
        var yrW = textPath(yr, { x: 0, y: ty, cap: D.cap, font: D.font, colour: D.colour }, c).w;
        var x = B.x + B.w / 2 - (D.gap + slash.w + D.gap + slash.w + 1.4 + yrW) / 2 + D.gap;
        out += textPath('/', { x: x, y: ty, cap: D.cap, font: D.font, colour: D.colour }, c).svg;
        x += slash.w + D.gap;
        out += textPath('/', { x: x, y: ty, cap: D.cap, font: D.font, colour: D.colour }, c).svg;
        x += slash.w + 1.4;
        out += textPath(yr, { x: x, y: ty, cap: D.cap, font: D.font, colour: D.colour }, c).svg;
      }
      return out;
    }
    var cap = D.cap * 0.72, lx = B.label.x;
    var labW = Math.max.apply(null, T.rows.map(function (r) { return textPath(r + ':', { x: 0, y: 0, cap: cap, font: D.font, colour: D.colour }, c).w; }));
    T.rows.forEach(function (r, i) {
      var base = top + step * (i + 1) - step * 0.28;
      out += textPath(r + ':', { x: lx, y: base - cap, cap: cap, font: D.font, colour: D.colour }, c).svg;
      out += '<path d="M' + (lx + labW + 1.2).toFixed(3) + ' ' + base.toFixed(3) + 'H' + (B.x + B.w - 2.4).toFixed(3) + '" stroke="' + col(c, D.colour) + '" stroke-width="0.22" stroke-dasharray="0.6 0.5"/>';
    });
    return out;
  }

  /* ---------- the sticker ---------- */
  function render(tpl, st, opts) {
    opts = opts || {};
    curFam = fonts[st.font] && fonts[st.font].extrabold ? st.font : 'poppins';
    tooSmall = opts.track ? {} : null;
    var c = resolveColours(st.colours), S = tpl.slots, parts = [];
    parts.push('<rect x="-1.5" y="-1.5" width="103" height="63" fill="#FFFFFF"/>');
    if (tpl.back) parts.push(tpl.back(c));

    var cl = cluster(S, st, c);
    // the logo area gives way to whatever sits top right
    var L = Object.assign({}, S.logo), limit = cl.left - 2.2;
    if (L.x + L.w > limit) {
      if (L.align === 'center') {
        var mid = 50, half = Math.min(L.w / 2, limit - mid);
        if (half >= 14) { L.x = mid - half; L.w = half * 2; } else { L.align = 'left'; L.x = Math.min(L.x, 4); L.w = limit - L.x; }
      } else L.w = limit - L.x;
    }
    if (st.logo) {
      // place the artwork itself (trimmed of any empty margin in the file), not the whole image
      var bx = st.logo.box || { x0: 0, y0: 0, x1: 1, y1: 1 }, LW = st.logo.w, LH = st.logo.h;
      var cw = (bx.x1 - bx.x0) * LW, ch = (bx.y1 - bx.y0) * LH;
      var k = Math.min(L.w / cw, L.h / ch) * st.logo.scale;
      var lw = cw * k, lh = ch * k;
      var lx = L.align === 'center' ? L.x + (L.w - lw) / 2 : L.align === 'right' ? L.x + L.w - lw : L.x;
      var ly = L.y + (L.h - lh) / 2;
      lx += st.logo.dx; ly += st.logo.dy;
      st.logo.printedWidthMm = lw * LW / cw;
      parts.push('<svg x="' + lx.toFixed(3) + '" y="' + ly.toFixed(3) + '" width="' + lw.toFixed(3) + '" height="' + lh.toFixed(3) + '" viewBox="' + (bx.x0 * LW).toFixed(2) + ' ' + (bx.y0 * LH).toFixed(2) + ' ' + cw.toFixed(2) + ' ' + ch.toFixed(2) + '" preserveAspectRatio="none" overflow="hidden">' +
        '<image href="' + st.logo.src + '" width="' + LW + '" height="' + LH + '" preserveAspectRatio="none"/></svg>');
    } else if (st.fields.name) {
      var nx = L.align === 'center' ? L.x + L.w / 2 : L.x, na = L.align === 'center' ? 'center' : 'left';
      var ns = { x: nx, y: L.y + L.h * 0.28, cap: L.h * 0.38, maxW: L.w, align: na, font: 'extrabold', colour: 'primary', minCap: MIN_CAP.name, warnKey: 'name' };
      var words = st.fields.name.split(' ').filter(Boolean), one = textPath(st.fields.name, Object.assign({}, ns, { maxW: 0 }), c);
      if (one.w > L.w * 1.15 && words.length > 1) {
        var best = null, cap2 = L.h * 0.27;
        for (var wi = 1; wi < words.length; wi++) {
          var a1 = words.slice(0, wi).join(' '), a2 = words.slice(wi).join(' ');
          var m = Math.max(textPath(a1, Object.assign({}, ns, { cap: cap2, maxW: 0 }), c).w, textPath(a2, Object.assign({}, ns, { cap: cap2, maxW: 0 }), c).w);
          if (!best || m < best.m) best = { m: m, a: a1, b: a2 };
        }
        var y1 = L.y + (L.h - cap2 * 2.6) / 2;
        parts.push(textPath(best.a, Object.assign({}, ns, { y: y1, cap: cap2 }), c).svg + textPath(best.b, Object.assign({}, ns, { y: y1 + cap2 * 1.6, cap: cap2 }), c).svg);
      } else parts.push(textPath(st.fields.name, ns, c).svg);
    } else if (!opts.forPrint) {
      parts.push('<rect x="' + L.x + '" y="' + L.y + '" width="' + L.w + '" height="' + L.h + '" rx="1.5" fill="none" stroke="#9AA3AD" stroke-width="0.3" stroke-dasharray="1.2 0.8"/>' +
        textPath('Your logo', { x: L.x + L.w / 2, y: L.y + L.h / 2 - 1.2, cap: 2.4, align: 'center', font: 'medium', colour: '#9AA3AD', family: 'poppins' }, c).svg);
    }
    parts.push(cl.svg);

    ['phone', 'email', 'web'].forEach(function (k) {
      var v = st.fields[k]; if (!v || !S[k]) return;
      if (S[k].icon) parts.push(icon(k, S[k].icon, c, st.iconStyle));
      parts.push(textPath(v, S[k].text, c, { minCap: MIN_CAP[k], warnKey: k }).svg);
    });

    var B = S.box;
    if (B) {
      parts.push('<rect x="' + B.x + '" y="' + B.y + '" width="' + B.w + '" height="' + B.h + '" rx="' + B.r + '" fill="#FFFFFF" stroke="' + col(c, B.stroke) + '" stroke-width="' + B.sw + '"/>');
      parts.push(textPath(st.fields.boxLabel || (TYPES[st.type] || TYPES.due).label, Object.assign({ maxW: B.w - 4, minCap: MIN_CAP.boxLabel, warnKey: 'boxLabel' }, B.label), c).svg);
      parts.push(boxRows(B, st, c));
    }

    if (S.callout && st.fields.callout) parts.push(textPath(st.fields.callout, S.callout, c, { minCap: MIN_CAP.callout, warnKey: 'callout' }).svg);
    if (tpl.front) parts.push(tpl.front(c));

    var cut = '<rect x="0" y="0" width="100" height="60" rx="3" fill="none" stroke="#E0007A" stroke-width="0.15" stroke-dasharray="1 0.6"/>';
    var clip = '<clipPath id="ssc-card-' + (opts.idSuffix || 'p') + '"><rect x="' + (opts.forPrint ? -1.5 : 0) + '" y="' + (opts.forPrint ? -1.5 : 0) + '" width="' + (opts.forPrint ? 103 : 100) + '" height="' + (opts.forPrint ? 63 : 60) + '" rx="' + (opts.forPrint ? 0 : 3) + '"/></clipPath>';
    var vb = opts.forPrint ? '-1.5 -1.5 103 63' : '-0.4 -0.4 100.8 60.8';
    var wh = opts.forPrint ? ' width="103mm" height="63mm"' : '';
    var meta = opts.forPrint ? '<desc>' + esc(JSON.stringify({ template: tpl.id, type: st.type, font: st.font, icons: st.iconStyle, size: '100x60mm', bleed: '1.5mm', fields: st.fields, colours: st.colours })) + '</desc>' : '';
    return '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="' + vb + '"' + wh + '>' + meta +
      '<defs>' + clip + '</defs><g clip-path="url(#ssc-card-' + (opts.idSuffix || 'p') + ')">' + parts.join('') + '</g>' +
      (opts.forPrint ? '' : cut) + '</svg>';
  }

  /* ---------- image uploads ---------- */
  function readImage(file) {
    return new Promise(function (resolve, reject) {
      var r = new FileReader();
      r.onload = function () {
        var img = new Image();
        img.onload = function () { resolve({ src: r.result, w: img.naturalWidth || 400, h: img.naturalHeight || 200, px: img.naturalWidth, isSvg: /svg/.test(file.type), scale: 1, dx: 0, dy: 0, original: r.result, name: file.name }); };
        img.onerror = reject; img.src = r.result;
      };
      r.onerror = reject; r.readAsDataURL(file);
    });
  }
  var PDFJS = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/', scripts = {};
  function loadScript(src) {
    return scripts[src] || (scripts[src] = new Promise(function (res, rej) { var t = document.createElement('script'); t.src = src; t.onload = res; t.onerror = rej; document.head.appendChild(t); }));
  }
  function pdfToImage(file) {
    return loadScript(PDFJS + 'pdf.min.js').then(function () {
      var lib = window.pdfjsLib; lib.GlobalWorkerOptions.workerSrc = PDFJS + 'pdf.worker.min.js';
      return file.arrayBuffer().then(function (buf) { return lib.getDocument({ data: buf }).promise; });
    }).then(function (pdf) { return pdf.getPage(1); }).then(function (page) {
      var v = page.getViewport({ scale: 1 }), vp = page.getViewport({ scale: Math.min(10, 3000 / Math.max(v.width, v.height)) });
      var cv = document.createElement('canvas'); cv.width = Math.round(vp.width); cv.height = Math.round(vp.height);
      return page.render({ canvasContext: cv.getContext('2d'), viewport: vp, background: 'rgba(0,0,0,0)' }).promise.then(function () {
        var src = cv.toDataURL('image/png');
        return { src: src, w: cv.width, h: cv.height, px: cv.width, isSvg: false, scale: 1, dx: 0, dy: 0, original: src, name: file.name, fromPdf: true };
      });
    });
  }
  function readLogoFile(file) {
    if (/pdf|illustrator|postscript/i.test(file.type) || /\.(pdf|ai)$/i.test(file.name)) return pdfToImage(file);
    if (!/^image\//.test(file.type) && !/\.(png|jpe?g|svg|webp|gif)$/i.test(file.name)) return Promise.reject(new Error('type'));
    return readImage(file);
  }
  var EMOJI = (function () { try { return new RegExp('[\\p{Extended_Pictographic}\\uFE0F\\u200D]', 'gu'); } catch (e) { return null; } })();

  /* ---------- background removal ----------
   * Finds the background from the image's edges, then removes only background that touches the
   * edges (a flood fill), so white lettering inside a logo survives. Edge pixels are faded out and
   * have the background colour taken back out of them, so there's no halo. */
  function loadImg(src) { return new Promise(function (res, rej) { var i = new Image(); i.onload = function () { res(i); }; i.onerror = rej; i.src = src; }); }
  function dist(p, o, c) { var a = p[o] - c[0], b = p[o + 1] - c[1], d = p[o + 2] - c[2]; return Math.sqrt(a * a + b * b + d * d); }
  // The "outside" of the artwork: solid pixels on the image edge, or touching a see-through pixel.
  // Reading the background here (not just the image edge) copes with logos cropped tight to the edge
  // and with PNGs that have a white box sitting inside a see-through border.
  function outerPixels(p, w, h) {
    var out = [], j, x, y;
    function clear(k) { return p[k * 4 + 3] < 128; }
    for (y = 0; y < h; y++) for (x = 0; x < w; x++) {
      j = y * w + x; if (clear(j)) continue;
      if (x === 0 || y === 0 || x === w - 1 || y === h - 1 || clear(j - 1) || clear(j + 1) || clear(j - w) || clear(j + w)) out.push(j);
    }
    return out;
  }
  function detectBackground(src) {
    return loadImg(src).then(function (img) {
      var k = Math.min(1, 400 / Math.max(img.naturalWidth, img.naturalHeight)), w = Math.max(2, Math.round(img.naturalWidth * k)), h = Math.max(2, Math.round(img.naturalHeight * k));
      var cv = document.createElement('canvas'); cv.width = w; cv.height = h; var x = cv.getContext('2d');
      x.imageSmoothingEnabled = false;                    // shrinking would blur a busy background into a flat one
      x.drawImage(img, 0, 0, w, h);
      var p = x.getImageData(0, 0, w, h).data, edge = outerPixels(p, w, h);
      if (!edge.length) return { transparent: true };
      var counts = {}, best = null;
      edge.forEach(function (j) {
        var o = j * 4, key = (p[o] >> 3) + ',' + (p[o + 1] >> 3) + ',' + (p[o + 2] >> 3), c = counts[key] || (counts[key] = { n: 0, r: 0, g: 0, b: 0 });
        c.n++; c.r += p[o]; c.g += p[o + 1]; c.b += p[o + 2];
        if (!best || c.n > best.n) best = c;
      });
      var bg = [best.r / best.n, best.g / best.n, best.b / best.n], near = 0;
      edge.forEach(function (j) { if (dist(p, j * 4, bg) < 40) near++; });
      // light = bright and close to neutral (saturation maths misbehaves next to pure white, e.g. JPEG white of 254.98)
      var share = near / edge.length, lo = Math.min.apply(null, bg), hi = Math.max.apply(null, bg), light = lo > 205 && hi - lo < 45;
      // white/light: remove when it's clearly the backdrop; a strong colour only when it's all around (it may be the design)
      return { solid: light ? share > 0.2 : share > 0.88, colour: bg, light: light };
    });
  }
  function removeBackground(src, bg) {
    return loadImg(src).then(function (img) {
      var k = Math.min(1, 3000 / Math.max(img.naturalWidth, img.naturalHeight)), w = Math.round(img.naturalWidth * k), h = Math.round(img.naturalHeight * k);
      var cv = document.createElement('canvas'); cv.width = w; cv.height = h; var x = cv.getContext('2d'); x.drawImage(img, 0, 0, w, h);
      var data = x.getImageData(0, 0, w, h), p = data.data, n = w * h, T1 = 26, T2 = 72;
      var seen = new Uint8Array(n), q = new Int32Array(n), qh = 0, qt = 0;
      function seed(j) { if (!seen[j] && p[j * 4 + 3] >= 16 && dist(p, j * 4, bg) < T2) { seen[j] = 1; q[qt++] = j; } }
      outerPixels(p, w, h).forEach(seed);
      while (qh < qt) {
        var j = q[qh++], o = j * 4, d = dist(p, o, bg);
        if (d <= T1) {
          p[o + 3] = 0;                                   // pure background: gone, and keep spreading
          var cx = j % w, cy = (j - cx) / w;
          if (cx > 0) seed(j - 1); if (cx < w - 1) seed(j + 1); if (cy > 0) seed(j - w); if (cy < h - 1) seed(j + w);
        } else {
          var a = (d - T1) / (T2 - T1);                   // soft edge: fade it and lift the background colour out
          for (var c = 0; c < 3; c++) p[o + c] = Math.max(0, Math.min(255, (p[o + c] - bg[c] * (1 - a)) / a));
          p[o + 3] = Math.round(p[o + 3] * a);
        }
      }
      x.putImageData(data, 0, 0);
      return cv.toDataURL('image/png');
    });
  }

  /* ---------- the UI ---------- */
  function h(tag, attrs, html) { var e = document.createElement(tag); for (var k in attrs || {}) e.setAttribute(k, attrs[k]); if (html != null) e.innerHTML = html; return e; }
  function chips(name, list) {
    return '<div class="ssc__chips" data-chips="' + name + '">' + Object.keys(list).map(function (k) { return '<button type="button" class="ssc__chip" data-v="' + k + '">' + esc(list[k].name || list[k]) + '</button>'; }).join('') + '</div>';
  }

  /* Three steps, one at a time: 1 logo, 2 details, 3 make it yours (design, extras, approve, buy).
   * Finished steps fold to a one-line summary you can tap to edit; later steps wait greyed out. */
  var STEP_TITLES = ['Upload your logo', 'Add your details', 'Make it yours'];
  var ICON_UP = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 15.5V4.5m0 0-4 4m4-4 4 4M5 14.5v3A2.5 2.5 0 0 0 7.5 20h9a2.5 2.5 0 0 0 2.5-2.5v-3"/></svg>';
  var ICON_TICK = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 12.5 4 4 8-9"/></svg>';
  function field(key, label, type, extra) {
    return '<label class="ssc__float"><input type="' + (type || 'text') + '" data-f="' + key + '" placeholder=" "' + (extra || '') + '><span>' + label + '</span></label>';
  }
  function row(title, sub, body) {
    return '<details class="ssc__row"><summary><span><b>' + title + '</b><small>' + sub + '</small></span><i aria-hidden="true"></i></summary><div class="ssc__row-body">' + body + '</div></details>';
  }
  function stepShell(n, body, cta) {
    return '<section class="ssc__step" data-step="' + n + '">' +
      '<button type="button" class="ssc__head" data-goto="' + n + '"><span class="ssc__num">' + n + '</span><span class="ssc__tick">' + ICON_TICK + '</span>' +
      '<span class="ssc__titles"><span class="ssc__title">' + STEP_TITLES[n - 1] + '</span><span class="ssc__sum" data-sum="' + n + '"></span></span><span class="ssc__edit">Edit</span></button>' +
      '<div class="ssc__body"><div class="ssc__inner">' + body + (cta ? '<p class="ssc__err" data-err="' + n + '" hidden></p><button type="button" class="ssc__cta" data-next="' + n + '">' + cta + '</button>' : '') + '</div></div></section>';
  }
  var ICON_STYLES = { circle: 'Solid', ring: 'Outline', plain: 'Plain' };
  var FREE_MAIL = /^(gmail|googlemail|hotmail|outlook|live|msn|yahoo|ymail|icloud|me|mac|aol|btinternet|btopenworld|sky|talktalk|virginmedia|ntlworld|blueyonder|tiscali|mail|gmx|protonmail|proton|zoho)\./i;
  var PHONE_OK = /^[+()\d\s-]{9,20}$/, EMAIL_OK = /^[^\s@]+@[^\s@]+\.[^\s@]+$/, REG_OK = /^\d{5,7}$/, OFTEC_OK = /^[A-Za-z]?\d{3,7}$/;

  function Customiser(root) {
    var self = this;
    var tpls = window.SSC_TEMPLATES;
    var st = this.state = {
      tpl: tpls[0].id, type: 'due', font: 'poppins', iconStyle: 'circle', auto: true,
      colours: Object.assign({}, tpls[0].colours),
      logo: null, badges: [],
      fields: { name: '', phone: '', email: '', web: '', gasSafe: false, reg: '', oftec: false, oftecReg: '', qr: '', qrCaption: QR_CAPTIONS[0], callout: STRAPLINES[0], boxLabel: TYPES.due.label },
      approved: false
    };
    this.root = root;
    this.step = 1; this.reached = 1;
    this.form = document.getElementById(root.getAttribute('data-form-id')) || root.closest('form') || document.querySelector('form[id^="product-form-"]:not([id*="installment"])');
    this.restored = this.restoreLink() || this.restore();   // a re-order link wins over what this device saved

    var step1 =
      '<label class="ssc__drop" data-drop><input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp,application/pdf,.pdf,.ai" data-logo>' + ICON_UP +
      '<b>Drop your logo here, or tap to choose</b><span>We design your sticker around it: your colours, your style. PNG, JPG, SVG or PDF.</span></label>' +
      '<div class="ssc__chip-logo" data-logo-chip hidden><img alt="" data-logo-thumb><span><b data-logo-name></b><small>Colours and style matched to your logo</small></span><button type="button" class="ssc__link" data-change>Change</button></div>' +
      '<p class="ssc__bg" data-bg hidden><span data-bg-text></span> <button type="button" class="ssc__link" data-bg-toggle></button></p>' +
      '<details class="ssc__more" data-logo-tools hidden><summary>Adjust logo</summary>' +
      '<label class="ssc__switch"><input type="checkbox" data-auto><i></i><span>Match colours, icons and font to my logo</span></label>' +
      '<label class="ssc__range">Size <input type="range" min="0.5" max="1.3" step="0.01" value="1" data-ls></label>' +
      '<label class="ssc__range">Left / right <input type="range" min="-12" max="12" step="0.1" value="0" data-lx></label>' +
      '<label class="ssc__range">Up / down <input type="range" min="-6" max="6" step="0.1" value="0" data-ly></label>' +
      '<button type="button" class="ssc__link" data-logo-clear>Remove logo</button></details>' +
      '<details class="ssc__more" data-noname><summary>No logo? Use your business name instead</summary>' + field('name', 'Business name', 'text', ' maxlength="40"') + '</details>';
    var step2 =
      field('phone', 'Phone number', 'tel', ' maxlength="20" autocomplete="tel"') +
      field('email', 'Email (optional)', 'email', ' maxlength="60" autocomplete="email"') +
      field('web', 'Website (optional)', 'text', ' maxlength="60" autocomplete="url"') + '<p class="ssc__hint2" data-web-auto hidden>Added from your email address.</p>' +
      '<label class="ssc__switch ssc__switch--card"><input type="checkbox" data-f="gasSafe"><i></i><span><b>Gas Safe registered</b><small>Adds the official Gas Safe mark with your number</small></span></label>' +
      '<div data-reg hidden>' + field('reg', 'Gas Safe registration number', 'text', ' maxlength="7" inputmode="numeric"') + '</div>' +
      (window.SSC_OFTEC ? '<label class="ssc__switch ssc__switch--card"><input type="checkbox" data-f="oftec"><i></i><span><b>OFTEC registered</b><small>Adds the official OFTEC mark with your number</small></span></label>' +
      '<div data-oftec-reg hidden>' + field('oftecReg', 'OFTEC registration number', 'text', ' maxlength="8"') + '</div>' : '');
    // Step 3 keeps to the decisions that matter: a design, how many, approve. Everything else has a
    // sensible default and waits behind one "More options" line.
    var step3 =
      '<p class="ssc__label">Choose a design</p><div class="ssc__tpls" data-tpls></div>' +
      '<details class="ssc__opts" data-opts><summary><span><b>More options</b><small>What goes in the box, QR code, badges, wording, colours and font</small></span><i aria-hidden="true"></i></summary>' +
      '<div class="ssc__opts-body"><p class="ssc__label">In the box</p>' + chips('type', TYPES) +
      '<div class="ssc__rows">' +
      row('Colours and font', 'Matched to your logo, or choose your own',
        '<div class="ssc__palettes" data-pals></div><div class="ssc__pickers">' + ['primary', 'secondary', 'accent', 'ink'].map(function (k) {
          return '<label>' + { primary: 'Main', secondary: 'Second', accent: 'Accent', ink: 'Text' }[k] + '<input type="color" data-c="' + k + '"></label>';
        }).join('') + '</div><p class="ssc__label">Font</p>' + chips('font', FAMILIES) + '<p class="ssc__label">Icons</p>' + chips('iconStyle', ICON_STYLES)) +
      row('QR code', 'Link to bookings, reviews or your website',
        field('qr', 'Link for the QR code', 'url', ' maxlength="120"') +
        '<label class="ssc__float ssc__float--select"><select data-f="qrCaption">' + QR_CAPTIONS.map(function (s) { return '<option value="' + esc(s) + '">' + (s || 'No text') + '</option>'; }).join('') + '</select><span>Text under it</span></label>') +
      row('Other badges', 'Boiler brand schemes and other memberships',
        '<label class="ssc__file"><input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" data-badge><span>Upload a badge (up to 2). Only add badges you\'re registered for.</span></label><div class="ssc__badges" data-badges></div>') +
      row('Wording', 'Strapline and box heading',
        field('callout', 'Strapline', 'text', ' maxlength="34" list="ssc-straps"') + '<datalist id="ssc-straps">' + STRAPLINES.map(function (s) { return '<option value="' + esc(s) + '">'; }).join('') + '</datalist>' +
        field('boxLabel', 'Box heading', 'text', ' maxlength="34"')) +
      '</div></div></details>' +
      '<div data-ssc-slot="checkout"></div>' +
      '<label class="ssc__approve"><input type="checkbox" data-approve><span><b>I approve this design</b><small>I\'ve checked the spelling and numbers. You\'ll print exactly what I see.</small></span></label>' +
      '<div data-ssc-slot="buy"></div>';

    root.innerHTML =
      '<div class="ssc__preview"><div class="ssc__stage" data-stage></div><p class="ssc__example" data-example>Example design. Upload your logo to see yours.</p><div class="ssc__warn" data-warn hidden></div></div>' +
      '<div class="ssc__controls">' +
      '<p class="ssc__restored" data-restored hidden>Welcome back. We kept your design. <button type="button" class="ssc__link" data-reset>Start again</button></p>' +
      stepShell(1, step1, 'Continue') + stepShell(2, step2, 'Continue') + stepShell(3, step3, '') +
      '</div>';

    this.stage = root.querySelector('[data-stage]');
    this.warn = root.querySelector('[data-warn]');
    this.exampleNote = root.querySelector('[data-example]');   // kept: the preview may be moved elsewhere on the page
    var Q = function (s) { return root.querySelector(s); };

    var tplBox = Q('[data-tpls]');
    tpls.forEach(function (t) {
      var b = h('button', { type: 'button', class: 'ssc__tpl', 'data-id': t.id });
      b.innerHTML = '<span class="ssc__thumb"></span><span>' + esc(t.name) + '</span>';
      b.onclick = function () { st.tpl = t.id; self.syncInputs(); self.update(); };   // a design never changes your colours
      tplBox.appendChild(b);
    });
    Q('[data-pals]').addEventListener('click', function (e) {
      var b = e.target.closest('[data-pal]'); if (!b) return;
      var i = b.getAttribute('data-pal');
      if (i === 'logo' && st.logoStyle) { st.colours = Object.assign({}, st.logoStyle.colours); st.auto = true; }
      else { var p = window.SSC_PALETTES[+i]; st.colours = { primary: p.primary, secondary: p.secondary, accent: p.accent, ink: p.ink }; st.auto = false; }
      self.syncInputs(); self.update();
    });
    Q('[data-opts]').addEventListener('toggle', function () { if (this.open) self.loadFontSamples(); });
    root.querySelectorAll('[data-chips]').forEach(function (box) {
      var key = box.getAttribute('data-chips');
      box.addEventListener('click', function (e) {
        var b = e.target.closest('[data-v]'); if (!b) return;
        var v = b.getAttribute('data-v');
        if (key === 'type') { var old = TYPES[st.type].label; if (!st.fields.boxLabel || st.fields.boxLabel === old) st.fields.boxLabel = TYPES[v].label; }
        st[key] = v;
        if (key === 'font') loadFamily(v).then(function () { self.update(); });
        self.syncInputs(); self.update();
      });
    });
    root.querySelectorAll('[data-f]').forEach(function (inp) {
      var k = inp.getAttribute('data-f');
      var on = function () {
        if (EMOJI && inp.type !== 'checkbox' && inp.value.search(EMOJI) > -1) { inp.value = inp.value.replace(EMOJI, ''); self.toast('Emojis can\'t be printed, so we\'ve left them out.'); }
        st.fields[k] = inp.type === 'checkbox' ? inp.checked : inp.value.trim();
        if (k === 'web') st.fields.web = st.fields.web.replace(/^https?:\/\//i, '').replace(/\/+$/, '');
        if (k === 'web') st.webAuto = false;
        if (k === 'email') self.webFromEmail();
        self.clearErr(); self.update();
      };
      inp.addEventListener('input', on); inp.addEventListener('change', on);
      if (inp.tagName === 'INPUT' && inp.type !== 'checkbox') inp.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); var b = inp.closest('.ssc__step').querySelector('[data-next]'); if (b) b.click(); }
      });
    });
    root.querySelectorAll('[data-c]').forEach(function (inp) {
      inp.addEventListener('input', function () { st.colours[inp.getAttribute('data-c')] = inp.value.toUpperCase(); st.auto = false; Q('[data-auto]').checked = false; self.update(); });
    });

    var logoIn = Q('[data-logo]'), drop = Q('[data-drop]');
    ['dragenter', 'dragover'].forEach(function (ev) { drop.addEventListener(ev, function (e) { e.preventDefault(); drop.classList.add('is-over'); }); });
    ['dragleave', 'drop'].forEach(function (ev) { drop.addEventListener(ev, function (e) { e.preventDefault(); drop.classList.remove('is-over'); }); });
    drop.addEventListener('drop', function (e) { var f = e.dataTransfer && e.dataTransfer.files[0]; if (f) self.takeLogo(f); });
    logoIn.addEventListener('change', function () { var f = logoIn.files[0]; if (f) self.takeLogo(f); });
    Q('[data-change]').onclick = function () { logoIn.click(); };
    this.takeLogo = function (f) {
      root.classList.add('ssc--busy');
      readLogoFile(f).then(function (l) {
        st.logo = l; st.auto = true;
        ['ls', 'lx', 'ly'].forEach(function (k) { Q('[data-' + k + ']').value = k === 'ls' ? 1 : 0; });
        if (l.isSvg) return self.applyLogoStyle();
        // a plain light background is removed straight away; a coloured one might be part of the design, so we ask
        return detectBackground(l.original).then(function (b) {
          l.bg = b.solid ? { colour: b.colour, removed: false } : null;
          if (b.solid && b.light) return removeBackground(l.original, b.colour).then(function (u) { l.src = u; l.bg.removed = true; });
        }).catch(function () {}).then(function () { return self.applyLogoStyle(); });
      }).then(function () { root.classList.remove('ssc--busy'); self.clearErr(); self.syncInputs(); self.update(); self.reveal(); }, function () {
        // a file we can't open must never leave the designer stuck
        root.classList.remove('ssc--busy'); logoIn.value = '';
        self.showErr(1, { msg: 'We couldn\'t open that file. Please use a PNG, JPG, SVG or PDF of your logo.' });
      });
    };
    Q('[data-auto]').addEventListener('change', function (e) {
      st.auto = e.target.checked;
      if (st.auto) self.applyLogoStyle().then(function () { self.syncInputs(); self.update(); });
      else { st.colours = Object.assign({}, self.tpl().colours); st.font = 'poppins'; st.iconStyle = 'circle'; self.syncInputs(); self.update(); }
    });
    Q('[data-bg-toggle]').onclick = function () {
      var l = st.logo; if (!l || !l.bg) return;
      root.classList.add('ssc--busy');
      (l.bg.removed ? Promise.resolve(l.original) : removeBackground(l.original, l.bg.colour)).then(function (u) {
        l.src = u; l.bg.removed = !l.bg.removed;
        return self.applyLogoStyle();
      }).then(function () { root.classList.remove('ssc--busy'); self.syncInputs(); self.update(); self.reveal(); });
    };
    ['ls', 'lx', 'ly'].forEach(function (k) {
      Q('[data-' + k + ']').addEventListener('input', function (e) {
        var v = parseFloat(e.target.value); if (!st.logo) return;
        if (k === 'ls') st.logo.scale = v; if (k === 'lx') st.logo.dx = v; if (k === 'ly') st.logo.dy = v; self.update();
      });
    });
    Q('[data-logo-clear]').onclick = function () { st.logo = null; st.logoStyle = null; logoIn.value = ''; self.syncInputs(); self.update(); };

    var badgeIn = Q('[data-badge]');
    badgeIn.addEventListener('change', function () {
      var f = badgeIn.files[0]; if (!f) return;
      if (st.badges.length >= 2) { badgeIn.value = ''; return self.toast('You can add up to 2 badges. Remove one first.'); }
      readLogoFile(f).then(function (b) { st.badges = st.badges.concat([b]); badgeIn.value = ''; self.syncInputs(); self.update(); },
        function () { badgeIn.value = ''; self.toast('We couldn\'t open that badge. Please use a PNG, JPG, SVG or PDF.'); });
    });
    Q('[data-badges]').addEventListener('click', function (e) {
      var b = e.target.closest('[data-rm]'); if (!b) return;
      st.badges.splice(parseInt(b.getAttribute('data-rm'), 10), 1); self.syncInputs(); self.update();
    });
    Q('[data-reset]').onclick = function () { try { localStorage.removeItem(SAVE_KEY); } catch (e) {} location.href = location.pathname; };   // drop any re-order link too
    Q('[data-approve]').addEventListener('change', function (e) { st.approved = e.target.checked; root.classList.toggle('ssc--approved', st.approved); self.emit(); });

    root.addEventListener('click', function (e) {
      var n = e.target.closest('[data-next]'); if (n) { self.next(); return; }
      var g = e.target.closest('[data-goto]'); if (g) { var k = +g.getAttribute('data-goto'); if (k <= self.reached && k !== self.step) self.go(k); }
    });

    Q('[data-restored]').hidden = !this.restored;
    if (this.fromLink) Q('[data-restored]').firstChild.nodeValue = 'Your design from your last order is loaded. Check your details, then add it to your cart. ';
    if (this.restored) { this.step = this.reached = !this.check(1).ok ? 1 : !this.check(2).ok ? 2 : 3; }   // back where they left off
    this.syncInputs();
    this.layoutSteps(true);
    this.hookForm();
    if (this.pendingLogo) this.loadLinkedLogo();
  }

  Customiser.prototype.tpl = function () { var id = this.state.tpl; return window.SSC_TEMPLATES.filter(function (t) { return t.id === id; })[0] || window.SSC_TEMPLATES[0]; };

  /* ---------- steps ---------- */
  Customiser.prototype.check = function (n) {
    var st = this.state, f = st.fields;
    if (n === 1) return st.logo || f.name ? { ok: true } : { ok: false, msg: 'Upload your logo, or use your business name instead.' };
    if (n === 2) {
      var digits = (f.phone || '').replace(/\D/g, '');
      if (!f.phone) return { ok: false, msg: 'Add the phone number customers should call.', field: 'phone' };
      if (/[^+()\d\s-]/.test(f.phone)) return { ok: false, msg: 'Phone numbers can only use numbers, spaces, + and brackets.', field: 'phone' };
      if (digits.length < 10) return { ok: false, msg: 'That number looks too short. UK numbers have 10 or 11 digits.', field: 'phone' };
      if (digits.length > 13) return { ok: false, msg: 'That number looks too long.', field: 'phone' };
      if (f.email && !EMAIL_OK.test(f.email)) return { ok: false, msg: 'That email address doesn\'t look right.', field: 'email' };
      if (f.gasSafe && !REG_OK.test(f.reg || '')) return { ok: false, msg: 'Add your Gas Safe registration number (5 to 7 digits), or switch Gas Safe off.', field: 'reg' };
      if (f.oftec && window.SSC_OFTEC && !OFTEC_OK.test(f.oftecReg || '')) return { ok: false, msg: 'Add your OFTEC registration number, or switch OFTEC off.', field: 'oftecReg' };
      return { ok: true };
    }
    return st.approved ? { ok: true } : { ok: false, msg: 'Tick "I approve this design" to add it to your cart.' };
  };
  Customiser.prototype.next = function () {
    var c = this.check(this.step);
    if (!c.ok) { this.showErr(this.step, c); return false; }
    if (this.step < 3) this.go(this.step + 1);
    return true;
  };
  Customiser.prototype.go = function (n) {
    this.step = n; this.reached = Math.max(this.reached, n);
    this.layoutSteps();
    var sec = this.root.querySelector('.ssc__step[data-step="' + n + '"]'), self = this;
    setTimeout(function () {
      var r = sec.getBoundingClientRect();
      if (r.top < 80 || r.top > window.innerHeight * 0.55) window.scrollBy({ top: r.top - (window.innerWidth < 990 ? self.stickyOffset() : 90), behavior: 'smooth' });
      var first = sec.querySelector('.ssc__body input:not([type=file]):not([type=checkbox])');
      if (first && n === 2 && window.innerWidth >= 990) first.focus({ preventScroll: true });
    }, 320);
  };
  Customiser.prototype.stickyOffset = function () { var p = document.querySelector('.ssc__preview'); return p ? p.getBoundingClientRect().height + 16 : 90; };
  Customiser.prototype.layoutSteps = function (instant) {
    var self = this;
    this.root.classList.toggle('ssc--instant', !!instant);
    this.root.querySelectorAll('.ssc__step').forEach(function (s) {
      var n = +s.getAttribute('data-step');
      s.classList.toggle('is-open', n === self.step);
      s.classList.toggle('is-done', n < self.step || (n <= self.reached && n !== self.step && self.check(n).ok));
      s.classList.toggle('is-locked', n > self.reached);
      s.querySelector('.ssc__head').disabled = n > self.reached;
    });
    if (this.form) this.form.setAttribute('data-ssc-step', this.step);
    this.summaries(); this.emit();
    if (instant) setTimeout(function () { self.root.classList.remove('ssc--instant'); }, 50);
  };
  Customiser.prototype.summaries = function () {
    var st = this.state, f = st.fields, Q = this.root.querySelector.bind(this.root);
    Q('[data-sum="1"]').textContent = st.logo ? (st.logo.name || 'Your logo') : f.name || '';
    Q('[data-sum="2"]').textContent = [f.phone, f.email, f.gasSafe && f.reg ? 'Gas Safe ' + f.reg : '', f.oftec && f.oftecReg ? 'OFTEC ' + f.oftecReg : ''].filter(Boolean).join('  ·  ');
    Q('[data-sum="3"]').textContent = this.tpl().name + '  ·  ' + TYPES[st.type].name;
  };
  Customiser.prototype.showErr = function (n, c) {
    var e = this.root.querySelector('[data-err="' + n + '"]'), sec = this.root.querySelector('.ssc__step[data-step="' + n + '"]');
    if (e) { e.textContent = c.msg; e.hidden = false; }
    sec.classList.remove('is-shake'); void sec.offsetWidth; sec.classList.add('is-shake');
    if (c.field) { var i = sec.querySelector('[data-f="' + c.field + '"]'); if (i) { i.classList.add('is-bad'); i.focus(); } }
    if (n === 3) { var a = this.root.querySelector('.ssc__approve'); a.classList.remove('is-shake'); void a.offsetWidth; a.classList.add('is-shake'); a.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
  };
  Customiser.prototype.clearErr = function () {
    this.root.querySelectorAll('[data-err]').forEach(function (e) { e.hidden = true; });
    this.root.querySelectorAll('.is-bad').forEach(function (e) { e.classList.remove('is-bad'); });
  };
  Customiser.prototype.reveal = function () {
    var s = this.stage; s.classList.remove('is-new'); void s.offsetWidth; s.classList.add('is-new');
  };
  Customiser.prototype.emit = function () {
    this.root.dispatchEvent(new CustomEvent('ssc:state', { bubbles: true, detail: { step: this.step, reached: this.reached, ok: this.check(this.step).ok, approved: this.state.approved, example: this.view() !== this.state } }));
  };

  Customiser.prototype.applyLogoStyle = function () {
    var st = this.state; if (!st.logo) return Promise.resolve();
    return analyseLogo(st.logo.src).then(function (a) {
      if (!st.logo) return;
      st.logo.box = a.box;
      var s = styleFromLogo(a); st.logoStyle = s;
      if (!st.auto) return;
      st.colours = Object.assign({}, s.colours); st.iconStyle = s.iconStyle; st.font = s.font;
      return loadFamily(s.font);
    });
  };
  Customiser.prototype.syncInputs = function () {
    var st = this.state, root = this.root, Q = root.querySelector.bind(root);
    root.querySelectorAll('[data-c]').forEach(function (i) { i.value = st.colours[i.getAttribute('data-c')]; });
    root.querySelectorAll('[data-f]').forEach(function (i) { var v = st.fields[i.getAttribute('data-f')]; if (i.type === 'checkbox') i.checked = !!v; else if (document.activeElement !== i) i.value = v || ''; });
    root.querySelectorAll('[data-chips]').forEach(function (box) {
      var v = st[box.getAttribute('data-chips')];
      box.querySelectorAll('[data-v]').forEach(function (b) { b.classList.toggle('is-on', b.getAttribute('data-v') === v); });
    });
    Q('[data-logo-tools]').hidden = !st.logo;
    Q('[data-logo-chip]').hidden = !st.logo;
    Q('[data-drop]').hidden = !!st.logo;
    if (st.logo) { Q('[data-logo-thumb]').src = st.logo.src; Q('[data-logo-name]').textContent = st.logo.name || 'Your logo'; }
    var bg = st.logo && st.logo.bg;
    Q('[data-bg]').hidden = !bg;
    if (bg) {
      Q('[data-bg-text]').textContent = bg.removed ? 'Background removed.' : 'Your logo has a coloured background.';
      Q('[data-bg-toggle]').textContent = bg.removed ? 'Undo' : 'Remove background';
    }
    if (st.fields.name) Q('[data-noname]').open = true;
    Q('[data-auto]').checked = !!st.auto;
    Q('[data-web-auto]').hidden = !(st.webAuto && st.fields.web);
    this.paintChoices();
    Q('[data-badges]').innerHTML = st.badges.map(function (b, i) { return '<span class="ssc__badge"><img src="' + b.src + '" alt=""><button type="button" class="ssc__link" data-rm="' + i + '">Remove</button></span>'; }).join('');
  };
  // Colour circles (the logo's own scheme first and ringed when in use), and font and icon choices shown, not named.
  Customiser.prototype.paintChoices = function () {
    var st = this.state, root = this.root, cur = st.colours;
    function eq(p, q) { return ['primary', 'secondary', 'accent', 'ink'].every(function (k) { return (p[k] || '').toUpperCase() === (q[k] || '').toUpperCase(); }); }
    function same(p) { return eq(p, cur); }
    function dot(p) { return '<i style="background:' + p.primary + '"></i><i style="background:' + p.secondary + '"></i><i style="background:' + p.accent + '"></i>'; }
    var html = '';
    if (st.logoStyle) html += '<button type="button" class="ssc__pal ssc__pal--logo' + (same(st.logoStyle.colours) ? ' is-on' : '') + '" data-pal="logo" title="Your logo\'s colours"><span>' + dot(st.logoStyle.colours) + '</span><em>Your logo</em></button>';
    window.SSC_PALETTES.forEach(function (p, i) { if (st.logoStyle && eq(p, st.logoStyle.colours)) return; html += '<button type="button" class="ssc__pal' + (same(p) ? ' is-on' : '') + '" data-pal="' + i + '" title="' + esc(p.name) + '"><span>' + dot(p) + '</span></button>'; });
    root.querySelector('[data-pals]').innerHTML = html;
    var c = resolveColours(st.colours);
    root.querySelectorAll('[data-chips="iconStyle"] [data-v]').forEach(function (b) {
      var v = b.getAttribute('data-v');
      b.innerHTML = '<svg viewBox="0 0 12 12" aria-hidden="true">' + icon('phone', { cx: 6, cy: 6, r: 5, colour: 'accent' }, c, v) + '</svg>' + esc(ICON_STYLES[v]);
    });
    root.querySelectorAll('[data-chips="font"] [data-v]').forEach(function (b) {
      var v = b.getAttribute('data-v'), f = fonts[v] && fonts[v].bold, sample = '';
      if (f) { var t = textPath('Aa', { x: 0, y: 0, cap: 10, font: 'bold', colour: '#1D1D1F', family: v }, {}); sample = '<svg viewBox="-1 -1 ' + (t.w + 2) + ' 14" aria-hidden="true">' + t.svg + '</svg>'; }
      b.innerHTML = (sample || '<span class="ssc__aa">Aa</span>') + esc(FAMILIES[v].name);
    });
  };
  Customiser.prototype.loadFontSamples = function () {
    var self = this;
    Promise.all(Object.keys(FAMILIES).map(loadFamily)).then(function () { self.paintChoices(); });
  };
  // A business email gives us their website for free (but not gmail and the like).
  Customiser.prototype.webFromEmail = function () {
    var st = this.state, f = st.fields, m = /@([^@\s]+\.[^@\s]+)$/.exec(f.email || '');
    if (!m || FREE_MAIL.test(m[1])) { if (st.webAuto) { f.web = ''; } return this.syncInputs(); }
    if (!f.web || st.webAuto) { f.web = m[1].toLowerCase(); st.webAuto = true; this.syncInputs(); }
  };
  Customiser.prototype.update = function () {
    var st = this.state, root = this.root, view = this.view();
    if (!engineReady) { this.stage.innerHTML = '<div class="ssc__skeleton"></div>'; this.summaries(); this.emit(); return; }
    this.stage.innerHTML = render(this.tpl(), view, { track: view === st });
    var small = tooSmall || {};
    this.exampleNote.hidden = view === st;
    root.querySelector('[data-reg]').hidden = !st.fields.gasSafe;
    var oReg = root.querySelector('[data-oftec-reg]'); if (oReg) oReg.hidden = !st.fields.oftec;
    root.querySelectorAll('.ssc__tpl').forEach(function (b) {
      var t = window.SSC_TEMPLATES.filter(function (x) { return x.id === b.getAttribute('data-id'); })[0];
      b.classList.toggle('is-on', t.id === st.tpl);
      b.querySelector('.ssc__thumb').innerHTML = render(t, view, { idSuffix: 't' + t.id });   // every design shown in your colours
    });
    // approval resets whenever the design changes
    if (st.approved) { st.approved = false; root.querySelector('[data-approve]').checked = false; root.classList.remove('ssc--approved'); }
    var w = [];
    if (st.logo && !st.logo.isSvg && st.logo.printedWidthMm) {
      var dpi = st.logo.px / (st.logo.printedWidthMm / 25.4);
      if (dpi < MIN_DPI) w.push('Your logo is quite small (about ' + Math.round(dpi) + ' dpi at this size) so it may print a little soft. A larger image or an SVG will print sharper.');
    }
    if (st.fields.qr && !/^(https?:\/\/)?[^\s.]+\.[^\s]{2,}/i.test(st.fields.qr)) w.push('The QR code link doesn\'t look like a web address. Check it before you order.');
    if (st.fields.qr && st.fields.qr.length > 80) w.push('Long links make a busy QR code. A shorter link will scan more easily.');
    var SMALL = {
      name: 'Your business name is long, so it prints small. A shorter version will read better.',
      phone: 'Your phone number is too long to print at a readable size.',
      email: 'Your email address is too long to read clearly on the sticker. A shorter one, like info@..., works best.',
      web: 'Your website is too long to read clearly. Try leaving out the www.',
      callout: 'Your strapline is long, so it prints small. Something shorter will stand out more.',
      boxLabel: 'Your box heading is long, so it prints small. Try something shorter.'
    };
    Object.keys(SMALL).forEach(function (k) { if (small[k]) w.push(SMALL[k]); });
    this.warn.hidden = !w.length; this.warn.innerHTML = w.map(esc).join('<br>');
    this.summaries(); this.emit();
    this.save();
  };
  // Until they add a logo or name, show a finished example design rather than an empty box.
  Customiser.prototype.view = function () {
    var st = this.state, ex = samples[0];
    if (st.logo || st.fields.name || !ex || !ex.style) return st;
    var f = Object.assign({}, st.fields);
    ['phone', 'email', 'web'].forEach(function (k) { if (!f[k]) f[k] = ex.fields[k]; });
    if (!st.fields.gasSafe) { f.gasSafe = true; f.reg = ex.fields.reg; }
    return Object.assign({}, st, { logo: ex.logo, fields: f, colours: ex.style.colours, font: ex.style.font, iconStyle: ex.style.iconStyle });
  };
  Customiser.prototype.problems = function () {
    var p = [], self = this;
    [1, 2, 3].forEach(function (n) { var c = self.check(n); if (!c.ok) p.push({ step: n, c: c }); });
    return p;
  };
  Customiser.prototype.printSvg = function () { return render(this.tpl(), this.state, { forPrint: true, idSuffix: 'print' }); };

  /* Re-order links: the design's settings travel as base64url JSON (?ssc=...), the logo as the file
   * Shopify stored with the order (?logo=https://cdn.shopify.com/...). Badges don't travel. */
  var LINK_KEYS = ['tpl', 'type', 'font', 'iconStyle', 'auto', 'colours', 'logoStyle', 'webAuto'];
  function packDesign(st) {
    var d = { v: 1, fields: st.fields }, l = st.logo;
    LINK_KEYS.forEach(function (k) { d[k] = st[k]; });
    if (l) d.logo = { w: l.w, h: l.h, px: l.px, isSvg: l.isSvg, scale: l.scale, dx: l.dx, dy: l.dy, box: l.box, name: l.name };
    var s = unescape(encodeURIComponent(JSON.stringify(d)));
    return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
  function unpackDesign(s) {
    s = s.replace(/-/g, '+').replace(/_/g, '/'); while (s.length % 4) s += '=';
    return JSON.parse(decodeURIComponent(escape(atob(s))));
  }
  function dataUrlBlob(src) {
    var m = /^data:([^;,]+)(;base64)?,(.*)$/.exec(src || ''); if (!m) return null;
    var raw = m[2] ? atob(m[3]) : decodeURIComponent(m[3]), b = new Uint8Array(raw.length);
    for (var i = 0; i < raw.length; i++) b[i] = raw.charCodeAt(i) & 255;
    return new Blob([b], { type: m[1] });
  }
  Customiser.prototype.restoreLink = function () {
    try {
      var q = new URLSearchParams(location.search), s = q.get('ssc'); if (!s) return false;
      var d = unpackDesign(s); if (!d || !d.fields) return false;
      this.fromLink = true;
      var st = this.state;
      LINK_KEYS.forEach(function (k) { if (d[k] != null) st[k] = d[k]; });
      Object.assign(st.fields, d.fields);
      if (!TYPES[st.type]) st.type = 'due'; if (!FAMILIES[st.font]) st.font = 'poppins';
      var url = q.get('logo') || '';
      if (d.logo && /^https:\/\/(cdn\.shopify\.com|[^/]*capitallettersigns\.co\.uk)\//.test(url)) this.pendingLogo = { meta: d.logo, url: url };
      return true;
    } catch (e) { return false; }
  };
  Customiser.prototype.loadLinkedLogo = function () {
    var self = this, st = this.state, p = this.pendingLogo; this.pendingLogo = null;
    this.root.classList.add('ssc--busy');
    fetch(p.url).then(function (r) { if (!r.ok) throw new Error('logo'); return r.blob(); }).then(function (b) {
      return new Promise(function (res, rej) { var fr = new FileReader(); fr.onload = function () { res(fr.result); }; fr.onerror = rej; fr.readAsDataURL(b); });
    }).then(function (src) {
      st.logo = Object.assign({ scale: 1, dx: 0, dy: 0 }, p.meta, { src: src, original: src });
      ['ls', 'lx', 'ly'].forEach(function (k) { var i = self.root.querySelector('[data-' + k + ']'); if (i) i.value = st.logo[{ ls: 'scale', lx: 'dx', ly: 'dy' }[k]]; });
      self.step = self.reached = !self.check(1).ok ? 1 : !self.check(2).ok ? 2 : 3;
      self.layoutSteps(true); self.syncInputs(); self.update(); self.reveal();
    }).catch(function () {
      self.showErr(1, { msg: 'We couldn\'t load the logo from your last order. Please upload it again - everything else is as you had it.' });
    }).then(function () { self.root.classList.remove('ssc--busy'); });
  };

  /* Keep the design on this device so they can come back to it (and reorder the same one). */
  Customiser.prototype.save = function () {
    var st = this.state;
    clearTimeout(this._t);
    this._t = setTimeout(function () {
      var data = { tpl: st.tpl, type: st.type, font: st.font, iconStyle: st.iconStyle, auto: st.auto, colours: st.colours, logoStyle: st.logoStyle, webAuto: st.webAuto, fields: st.fields, logo: st.logo, badges: st.badges, at: Date.now() };
      try { localStorage.setItem(SAVE_KEY, JSON.stringify(data)); } catch (e) {
        try { data.logo = null; data.badges = []; localStorage.setItem(SAVE_KEY, JSON.stringify(data)); } catch (e2) { /* storage full or blocked */ }
      }
    }, 400);
  };
  Customiser.prototype.restore = function () {
    try {
      var d = JSON.parse(localStorage.getItem(SAVE_KEY) || 'null'); if (!d || !d.fields) return false;
      var st = this.state;
      ['tpl', 'type', 'font', 'iconStyle', 'auto', 'colours', 'logoStyle', 'webAuto', 'logo', 'badges'].forEach(function (k) { if (d[k] != null) st[k] = d[k]; });
      Object.assign(st.fields, d.fields);
      if (!TYPES[st.type]) st.type = 'due'; if (!FAMILIES[st.font]) st.font = 'poppins';
      return !!(d.fields.phone || d.fields.name || d.logo);
    } catch (e) { return false; }
  };

  /* Attach the design to the product form and post it the classic way (the AJAX cart can't carry files). */
  Customiser.prototype.hookForm = function () {
    var self = this, form = this.form; if (!form) return;
    function prop(name, value) {
      var i = form.querySelector('input[data-ssc-prop="' + name + '"]');
      if (!i) { i = h('input', { type: 'hidden', name: 'properties[' + name + ']', 'data-ssc-prop': name }); form.appendChild(i); }
      i.value = value;
    }
    form.addEventListener('submit', function (e) {
      e.preventDefault(); e.stopImmediatePropagation();
      if (self.sending) return;                 // a second tap must not add a second pack
      var p = self.problems();
      if (p.length) { if (p[0].step !== self.step) self.go(p[0].step); var pp = p[0]; setTimeout(function () { self.showErr(pp.step, pp.c); }, p[0].step !== self.step ? 340 : 0); return; }
      var st = self.state, f = st.fields;
      prop('Design', self.tpl().name + ' - ' + TYPES[st.type].name);
      prop('Phone', f.phone); prop('Email', f.email); prop('Website', f.web); prop('Strapline', f.callout);
      if (!st.logo && f.name) prop('Business name', f.name);
      prop('Gas Safe reg', f.gasSafe ? f.reg : 'Not shown');
      if (f.oftec && window.SSC_OFTEC) prop('OFTEC reg', f.oftecReg);
      if (f.qr) prop('QR link', f.qr);
      if (st.badges.length) prop('Extra badges', String(st.badges.length));
      prop('_Style', [FAMILIES[st.font].name, st.iconStyle, st.auto && st.logo ? 'from logo' : 'chosen'].join(' / '));
      prop('_Colours', [st.colours.primary, st.colours.secondary, st.colours.accent, st.colours.ink].join(' '));
      prop('_Approved', new Date().toISOString());
      // everything needed to rebuild this exact design later (re-order link in the email / account page)
      prop('_Reorder', 'ssc=' + packDesign(st));
      var file = form.querySelector('input[data-ssc-file]');
      if (!file) { file = h('input', { type: 'file', name: 'properties[Design file]', 'data-ssc-file': '1', hidden: 'hidden' }); form.appendChild(file); }
      var dt = new DataTransfer();
      dt.items.add(new File([self.printSvg()], 'service-sticker-design.svg', { type: 'image/svg+xml' }));
      file.files = dt.files;
      var lf = form.querySelector('input[data-ssc-logo-file]');
      if (!lf) { lf = h('input', { type: 'file', name: 'properties[_Logo file]', 'data-ssc-logo-file': '1', hidden: 'hidden' }); form.appendChild(lf); }
      var ldt = new DataTransfer(), lb = st.logo && dataUrlBlob(st.logo.src);
      if (lb) ldt.items.add(new File([lb], 'logo.' + (lb.type === 'image/svg+xml' ? 'svg' : lb.type === 'image/jpeg' ? 'jpg' : 'png'), { type: lb.type }));
      lf.files = ldt.files;
      lf.disabled = !lb;                        // no logo (business name only): send nothing
      form.setAttribute('enctype', 'multipart/form-data');
      self.sending = true; self.root.classList.add('ssc--sending');
      form.querySelectorAll('button[type="submit"]').forEach(function (b) { b.dataset.label = b.dataset.label || b.textContent; b.textContent = 'Adding to cart\u2026'; b.disabled = true; });
      document.dispatchEvent(new CustomEvent('ssc:sending'));
      if (self.onSubmit) return self.onSubmit(form);
      HTMLFormElement.prototype.submit.call(form);
    }, true);
  };

  // Coming back with the browser's Back button: let them buy again.
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    document.querySelectorAll('[data-ssc]').forEach(function (r) {
      if (!r.ssc) return; r.ssc.sending = false; r.classList.remove('ssc--sending');
      r.ssc.form && r.ssc.form.querySelectorAll('button[type="submit"]').forEach(function (b) { b.disabled = false; if (b.dataset.label) b.textContent = b.dataset.label; });
    });
    document.dispatchEvent(new CustomEvent('ssc:idle'));
  });
  Customiser.prototype.toast = function (msg) {
    var t = this.toastEl || (this.toastEl = h('div', { class: 'ssc-toast', role: 'status' }));
    if (!t.parentNode) document.body.appendChild(t);
    t.textContent = msg; t.classList.add('is-on');
    clearTimeout(this._tt); this._tt = setTimeout(function () { t.classList.remove('is-on'); }, 3800);
  };

  /* Example logos (made up) run through the real styling, for the empty preview and the showcase. */
  var samples = [];
  // Only the first example is needed to show the page; the rest load in the background for the showcase.
  function prepareSamples(which) {
    if (!samples.length) samples = (window.SSC_SAMPLES || []).map(function (x) { return Object.assign({}, x); });
    return Promise.all(samples.filter(function (x, i) { return which ? which(i) : true; }).map(function (x) {
      var src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(x.svg)));
      return new Promise(function (res) { var i = new Image(); i.onload = function () { res(i); }; i.onerror = function () { res(i); }; i.src = src; }).then(function (img) {
        x.logo = { src: src, original: src, w: img.naturalWidth || 400, h: img.naturalHeight || 140, isSvg: true, scale: 1, dx: 0, dy: 0 };
        return analyseLogo(src);
      }).then(function (a) {
        x.logo.box = a.box; x.style = styleFromLogo(a);
        if (x.colours) x.style.colours = Object.assign({}, x.colours);   // a real customer's sticker, in their real colours
        loadFamily(x.style.font).then(refreshAll);
      });
    }));
  }
  function showcase() {
    document.querySelectorAll('[data-ssc-showcase]').forEach(function (el) {
      el.innerHTML = samples.map(function (x, i) {
        var tpl = window.SSC_TEMPLATES.filter(function (t) { return t.id === x.tpl; })[0] || window.SSC_TEMPLATES[i % window.SSC_TEMPLATES.length];
        var st = { type: x.type || 'due', font: x.style.font, iconStyle: x.style.iconStyle, colours: x.style.colours, logo: x.logo, badges: [],
          fields: Object.assign({ name: '', gasSafe: true, reg: '123456', qr: '', qrCaption: '', callout: STRAPLINES[0], boxLabel: (TYPES[x.type] || TYPES.due).label }, x.fields) };
        return '<figure class="ssc-show__item"><div class="ssc-show__logo"><img src="' + x.logo.src + '" alt="Example logo"></div>' +
          '<div class="ssc-show__arrow" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M4 12h15m-5-5 5 5-5 5"/></svg></div><div class="ssc-show__sticker">' + render(tpl, st, { idSuffix: 'show' + i }) + '</div></figure>';
      }).join('');
    });
  }

  var engineReady = false;
  function refreshAll() { if (!engineReady) return; document.querySelectorAll('[data-ssc]').forEach(function (r) { if (r.ssc) r.ssc.update(); }); }
  function boot() {
    var roots = document.querySelectorAll('[data-ssc]'); if (!roots.length && !document.querySelector('[data-ssc-showcase]')) return;
    // the steps are usable straight away; the sticker appears as soon as the first font has arrived
    roots.forEach(function (r) { var c = new Customiser(r); r.ssc = c; c.update(); });
    document.dispatchEvent(new CustomEvent('ssc:ready'));
    Promise.all([loadFamily('poppins'), prepareSamples(function (i) { return i === 0; })]).then(function () {
      engineReady = true;
      roots.forEach(function (r) { var f = r.ssc.state.font; if (f !== 'poppins') loadFamily(f).then(refreshAll); r.ssc.syncInputs(); });
      refreshAll();
      return prepareSamples(function (i) { return i > 0; });
    }).then(showcase);
  }
  window.SSC = { render: render, boot: boot, fonts: fonts, analyseLogo: analyseLogo, styleFromLogo: styleFromLogo, loadFamily: loadFamily, detectBackground: detectBackground, removeBackground: removeBackground };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
